import { useProjectTeamId } from '@common/project/hooks/useProjectTeamId';
import { EllipsisFlexGrow } from '@common/ScrollableFlexGrow';
import { Badges, Ellipsis, Select, SelectProps, Stack, theme } from '@prophecy/ui';
import { useOnMount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { sortBy } from 'lodash-es';
import { useMemo } from 'react';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { Fabric } from '../../redux/types';
import { FabricWithProviderInfo, useUserFabrics, useWithFabricProviderInfo } from './hooks';
import { FabricSVGIcons } from './icons';
import { getFabricProviderMapValue } from './utils';

export const FabricSelect = ({
  fabrics: inputFabrics,
  showExpiry,
  disableExpired,
  teamId: _teamId,
  filterFn,
  selectFirstByDefault,
  ...props
}: SelectProps & {
  fabrics?: Fabric[];
  showExpiry?: boolean;
  disableExpired?: boolean;
  teamId?: string;
  filterFn?: (fabric: FabricWithProviderInfo) => boolean;
  selectFirstByDefault?: boolean;
}) => {
  const { isProfessionalEditionEnabled } = useGlobalVariables();
  const projectTeamId = useProjectTeamId();
  const teamId = isProfessionalEditionEnabled ? _teamId || projectTeamId : undefined;

  const persistentFilterFn = usePersistentCallback(filterFn || (() => true));
  const userFabrics = useUserFabrics();
  const fabricsWithProviderInfo = useWithFabricProviderInfo(inputFabrics || userFabrics);

  const fabricOptions = useMemo(() => {
    return sortBy(
      fabricsWithProviderInfo?.filter((fabric) => {
        if (teamId && fabric.fabric.team._id !== teamId) {
          return false;
        }
        return persistentFilterFn(fabric);
      }),
      'fabric.name'
    );
  }, [fabricsWithProviderInfo, teamId, persistentFilterFn]);

  useOnMount(() => {
    if (selectFirstByDefault && fabricOptions.length > 0 && !props.value) {
      const firstFabric = fabricOptions[0].fabric;
      props.onChange?.(firstFabric.id, {
        label: firstFabric.name,
        value: firstFabric.id
      });
    }
  });

  return (
    <Select placeholder={'Choose a fabric'} {...props}>
      {fabricOptions.map(({ fabricProviderInfo, fabric, isExpired }) => {
        const FabricProviderIcon = getFabricProviderMapValue(FabricSVGIcons, fabricProviderInfo);

        return (
          <Select.Option value={fabric.id} key={fabric.id} label={fabric.name} disabled={isExpired && disableExpired}>
            <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
              {FabricProviderIcon && <FabricProviderIcon name={fabricProviderInfo.provider} size='s' />}
              <EllipsisFlexGrow>
                <Ellipsis tooltip>{fabric.name}</Ellipsis>
              </EllipsisFlexGrow>
              {(showExpiry || disableExpired) && isExpired && (
                <Badges size='xs' tone='error'>
                  Expired
                </Badges>
              )}
            </Stack>
          </Select.Option>
        );
      })}
    </Select>
  );
};
